import { useState } from "react";

export default function AssetsStep({
  progress,
  estimatedTimeLeft,
  audioStatus,
  thumbnailStatus,
  onCancel,
}) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const isAssetsComplete = audioStatus === "completed" && thumbnailStatus === "completed";
  const isRendering = progress >= 80 && progress < 100;
  const hasFailed = audioStatus === "failed" || thumbnailStatus === "failed";

  const steps = [
    {
      id: "assets",
      title: "Generating audio & images",
      subtitle: audioStatus === "completed" 
        ? 'AI assets generation complete' 
        : audioStatus === "processing" 
        ? "Processing audio and images on server..." 
        : "Queued...",
      status: audioStatus,
    },
    {
      id: "download",
      title: "Downloading assets",
      subtitle: isAssetsComplete && !isRendering
        ? "Assets downloaded"
        : isAssetsComplete
        ? "Downloading files..."
        : "Waiting for generation...",
      status: isAssetsComplete ? (isRendering ? "completed" : "processing") : "pending",
    },
    {
      id: "render",
      title: "Rendering video locally",
      subtitle: isRendering
        ? "FFmpeg processing..."
        : progress === 100
        ? "Video ready!"
        : "Waiting to sync audio and visuals",
      status: isRendering ? "processing" : progress === 100 ? "completed" : "pending",
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        );
      case "processing":
        return (
          <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
      case "failed":
        return (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <div className="w-2 h-2 rounded-full bg-gray-400" />
        );
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "processing":
        return "bg-primary-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 sm:px-6 pb-24">

        {/* Icon */}
        <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2 text-center">
          Creating your video...
        </h1>
        <p className="text-sm text-gray-500 text-center mb-10 max-w-sm">
          AI is generating your video assets. This usually takes about a minute.
        </p>

        <div className="w-full max-w-lg">
          {/* Error banner */}
          {hasFailed && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800">Generation failed</p>
                <p className="text-xs text-red-600 mt-1">
                  {audioStatus === "failed" && thumbnailStatus === "failed"
                    ? "Audio and image generation failed. Please go back and try again."
                    : audioStatus === "failed"
                    ? "Audio generation failed. Please go back and try again."
                    : "Image generation failed. Please go back and try again."}
                </p>
              </div>
            </div>
          )}

          {/* Progress card */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-3xl font-bold text-primary-500">{progress.toFixed(0)}%</span>
              <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                Est. {estimatedTimeLeft}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Step cards */}
          <div className="flex flex-col gap-2">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  step.status === "processing"
                    ? "bg-primary-50 border-primary-100"
                    : step.status === "completed"
                    ? "bg-white border-gray-200"
                    : "bg-gray-50 border-gray-100"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStatusBg(step.status)}`}
                >
                  {getStatusIcon(step.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    step.status === "pending" ? "text-gray-400" : "text-gray-800"
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{step.subtitle}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Info note */}
          <div className="flex items-center gap-2 justify-center mt-6 text-xs text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Progress is automatically saved. Feel free to wait.
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <svg className="w-4 h-4 animate-pulse text-primary-500" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="4" />
            </svg>
            Processing assets...
          </span>
          {showCancelConfirm ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 hidden sm:inline">Are you sure?</span>
              <button
                onClick={() => { setShowCancelConfirm(false); onCancel(); }}
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
              >
                Yes, cancel
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Keep going
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
